import moment from 'moment';

// 회원권 선호도 집계 (통계 Box / AI transform 공용 단일 소스) //
// 입력:
//   - allMembers: [{ memberId, birthDay, usages: [{ memberShipId, memberShipName, startDate }] }]
//   - salesData:  [{ itemName, totalPrice, isRefund }]
//   - startDate / endDate: moment 또는 ISO 문자열 (산정기간) //
// 출력: { rankings, totalCount, totalRevenue, topCards, avgReRegRate }
//   - rankings[i]: { membershipId, membershipName, count, segments: [{ ageIndex, count, percent }] }
//   - topCards: { mostSelected:{name,count}, topRevenue:{name,revenue}, topReReg:{name,rate} } (없으면 null)
export function computeMembershipPreference({ allMembers, salesData, startDate, endDate }) {
  const members = Array.isArray(allMembers) ? allMembers : [];
  const sales = Array.isArray(salesData) ? salesData : [];

  const start = moment(startDate);
  const end = moment(endDate);

  const year = parseInt(moment().format('YYYY'));

  // 출생연도 → 연령대 인덱스 (구간을 벗어나면 -1) //
  function getAgeGroupIndex(birthYear) {
    const age = year - birthYear;

    if (age < 0 || age > 98) return -1;

    return Math.floor((age + 1) / 10);
  }

  // 회원권별 집계: 산정기간 내 등록한 회원을 회원권별로 그룹핑 (회원 1명은 회원권당 1회만 카운트) //
  const membershipMap = new Map();

  for (const member of members) {
    if (!member.birthDay || !Array.isArray(member.usages)) continue;

    const ageIndex = getAgeGroupIndex(parseInt(member.birthDay.slice(0, 4)));

    if (ageIndex === -1) continue;

    const countedMemberships = new Set();

    for (let j = 0; j < member.usages.length; j++) {
      const usage = member.usages[j];

      if (!usage.memberShipId || !usage.memberShipName) continue;
      if (!(start.isSameOrBefore(usage.startDate, 'day') && end.isSameOrAfter(usage.startDate, 'day'))) continue;
      if (countedMemberships.has(usage.memberShipId)) continue;

      countedMemberships.add(usage.memberShipId);

      let entry = membershipMap.get(usage.memberShipId);

      if (!entry) {
        entry = {
          membershipId: usage.memberShipId,
          membershipName: usage.memberShipName,
          count: 0,
          reRegisteredCount: 0,
          revenue: 0,
          ageCounts: new Array(10).fill(0),
        };
        membershipMap.set(usage.memberShipId, entry);
      }

      entry.count += 1;
      entry.ageCounts[ageIndex] += 1;
      // 첫 이용권(j===0)이 아니면 재등록 //
      if (j !== 0) entry.reRegisteredCount += 1;
    }
  }

  // 회원권별 매출 집계: 결제 상품명(itemName)이 회원권명과 일치하는 결제 합산 (환불 제외) //
  const revenueByName = new Map();

  for (const sale of sales) {
    if (sale.isRefund) continue;

    const prevRevenue = revenueByName.get(sale.itemName) || 0;

    revenueByName.set(sale.itemName, prevRevenue + (sale.totalPrice || 0));
  }

  const memberships = Array.from(membershipMap.values());

  let totalMemberCount = 0;
  let totalMembershipRevenue = 0;

  for (const membership of memberships) {
    membership.revenue = revenueByName.get(membership.membershipName) || 0;
    totalMemberCount += membership.count;
    totalMembershipRevenue += membership.revenue;
  }

  // 인원수 내림차순 정렬 → 랭킹 //
  memberships.sort((a, b) => b.count - a.count || a.membershipId - b.membershipId);

  // 랭킹 행 + 연령대 세그먼트 가공 //
  const rankings = memberships.map((membership) => {
    const segments = [];

    for (let i = 0; i < membership.ageCounts.length; i++) {
      if (membership.ageCounts[i] === 0) continue;

      segments.push({
        ageIndex: i,
        count: membership.ageCounts[i],
        percent: Math.round(membership.ageCounts[i] / membership.count * 100),
      });
    }

    return {
      membershipId: membership.membershipId,
      membershipName: membership.membershipName,
      count: membership.count,
      segments,
    };
  });

  // 상단 카드 + 평균 재등록률 산출 //
  let mostSelected = null;
  let topRevenue = null;
  let topReReg = null;
  let reRegRateSum = 0;
  let reRegRateCount = 0;

  for (const membership of memberships) {
    if (mostSelected === null || membership.count > mostSelected.count) mostSelected = membership;
    if (topRevenue === null || membership.revenue > topRevenue.revenue) topRevenue = membership;

    const reRegRate = membership.count > 0 ? membership.reRegisteredCount / membership.count : 0;

    reRegRateSum += reRegRate;
    reRegRateCount += 1;

    if (topReReg === null || reRegRate > topReReg.rate) topReReg = { membership, rate: reRegRate };
  }

  const topCards = {
    mostSelected: mostSelected ? { name: mostSelected.membershipName, count: mostSelected.count } : null,
    topRevenue: topRevenue ? { name: topRevenue.membershipName, revenue: topRevenue.revenue } : null,
    topReReg: topReReg ? { name: topReReg.membership.membershipName, rate: topReReg.rate } : null,
  };

  return {
    rankings,
    totalCount: totalMemberCount,
    totalRevenue: totalMembershipRevenue,
    topCards,
    avgReRegRate: reRegRateCount > 0 ? reRegRateSum / reRegRateCount : 0,
  };
}
